import { useEffect } from 'react';
import i18n from 'i18next';
import { syncService } from '../services/syncService';
import { SyncStatus } from '../types/sync/enums';
import { useSyncStore } from '../stores/syncStore';
import { useTenantStore } from '../stores/tenantStore';
import { consumeSyncStream } from '../utils/sync/consumeSyncStream';

/**
 * If a sync is already running on the server (started in another tab, or in progress across a page
 * reload), reattach to its progress stream so the UI reflects it. Mount this ONCE app-wide
 * (RootLayout) — mounting it per-consumer would open duplicate progress streams.
 *
 * `ready` must be the auth guard's readiness. Resuming before it is true is not merely early, it is
 * wrong: the tenant store is persisted, so on a reload into a different company it still holds the
 * previous one, and the status/stream calls would be stamped with that company's CIU. The effect is
 * also keyed on the tenant so a switch aborts the previous company's stream and reattaches to the
 * new one, rather than leaving the old connection feeding the shared sync store.
 *
 * Also the handoff for InitialSync's own dismissal at RelanceFill: InitialSync tracks the first sync
 * through its own local stream, wholly outside `syncStore`, and dismisses
 * (`setInitialSyncRequired(false)`) once content is usable — before the sync's own terminal snapshot.
 * At that point nothing has told this store a sync is even running. `initialSyncRequired` in this
 * effect's deps is what re-runs it the moment InitialSync unmounts: `getStatus` finds the sync still
 * `Running` and this hook picks up tracking it to the real terminal, or finds it ended with its relances
 * still filling in and hands that to useRelanceFillTracker.
 */
export function useResumeSync(ready: boolean, initialSyncRequired: boolean): void {
    const setSyncing = useSyncStore(s => s.setSyncing);
    const setProgress = useSyncStore(s => s.setProgress);
    const setSyncStatus = useSyncStore(s => s.setSyncStatus);
    const setSyncFailed = useSyncStore(s => s.setSyncFailed);
    const resetSync = useSyncStore(s => s.resetSync);
    const ciu = useTenantStore(s => s.ciu);
    const tenantSwitchedAt = useTenantStore(s => s.tenantSwitchedAt);

    useEffect(() => {
        // Still on the InitialSync screen: nothing to resume onto yet, and reattaching here would
        // race InitialSync's own stream over the same sync.
        if (initialSyncRequired) return;
        if (!ready || !ciu) return;

        // Whatever the previous company left behind is not this company's state.
        resetSync();

        const controller = new AbortController();
        // A superseded run must not write to the shared store at all: its `finally` would otherwise
        // land after the next company's run has already set `syncing`, clearing it back to idle.
        let cancelled = false;
        void (async () => {
            try {
                const status = await syncService.getStatus();
                if (cancelled) return;
                // A real-time sync that has ended but whose relances are still generating: useRelanceFillTracker
                // follows it from this snapshot.
                if (status.status === SyncStatus.Completed && status.filling) {
                    setProgress(status);
                    return;
                }
                if (status.status !== SyncStatus.Running) return;
                setSyncing(true);
                setProgress(status);
                await consumeSyncStream(controller, setProgress, setSyncStatus, (key, opts) => i18n.t(key, opts));
            } catch {
                // Best-effort resume — a failed status check just leaves the UI idle.
            } finally {
                if (!cancelled) {
                    setSyncing(false);
                    // Same derivation as useSync.trigger()'s finally — a resumed run that ends in one of
                    // consumeSyncStream's failure branches must also leave a persistent syncFailed behind.
                    const { progress: finished, syncStatus: message } = useSyncStore.getState();
                    if (message !== null && finished?.status !== SyncStatus.Completed) setSyncFailed(true);
                }
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, [ready, ciu, tenantSwitchedAt, initialSyncRequired, resetSync, setSyncing, setProgress, setSyncStatus, setSyncFailed]);
}
